import { id } from "date-fns/locale"

import type { SimpleMesh2d, Intersection2d } from "."
import { whereDoSegmentsIntersect } from ".."
import { findPairing } from "../../matching/findPairing"
import type { Edge2d, Edge2dComputed } from "../../mesh"
import { isSamePoint } from "../collision"

export const edgesSharePoint = (
  e1: Edge2dComputed,
  e2: Edge2dComputed,
): boolean => {
  const { p1: a, p2: b } = e1
  const { p1: c, p2: d } = e2

  return (
    isSamePoint(a, c) ||
    isSamePoint(a, d) ||
    isSamePoint(b, c) ||
    isSamePoint(b, d)
  )
}

export const getFirstIntersection = (
  mesh: SimpleMesh2d,
): Intersection2d | null => {
  const computeEdge = (edge: Edge2d): Edge2dComputed => ({
    ...edge,
    p1: mesh.nodes[edge.node1id],
    p2: mesh.nodes[edge.node2id],
  })

  return findPairing({
    elements: Object.entries(mesh.edges),
    produceResult: ([idA, edgeA], [idB, edgeB]) => {
      const edgeAComputed = computeEdge(edgeA)
      const edgeBComputed = computeEdge(edgeB)

      const intersectionPoint = whereDoSegmentsIntersect(
        edgeAComputed,
        edgeBComputed,
      )
      if (intersectionPoint !== null) {
        const identifiedIntersectionPoint = {
          ...intersectionPoint,
          id: idA + `---` + idB,
        }
        const intersectionIsRoundingError = Boolean(
          Object.values(mesh.nodes).find((node) =>
            isSamePoint(node, intersectionPoint),
          ),
        )

        if (!intersectionIsRoundingError) {
          return {
            node: identifiedIntersectionPoint,
            edgeA: [idA, { ...edgeA, ...edgeAComputed }],
            edgeB: [idB, { ...edgeB, ...edgeBComputed }],
          }
        }
      }

      return null
    },
  })
}
